import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import { AlertTriageIcon, CrossIcon } from "../../Icons";
import { ResponsiveModal } from "../../ResponsiveModal";
import { Typo } from "../../Typo";
import { withSupportLink } from "./supportLink";

type ProgramCapModalProps = {
  open: boolean;
  cap: number;
  onClose: () => void;
  onManage: () => void;
  isMobile?: boolean;
};

/**
 * Blocking modal shown instead of opening the Create Program wizard when the
 * target Sub IB has reached the program cap.
 */
export const ProgramCapModal = ({
  open,
  cap,
  onClose,
  onManage,
  isMobile = false,
}: ProgramCapModalProps) => {
  const { t } = useTranslation();

  const manageButton = (
    <ButtonV2
      variant="brand"
      size="md"
      onClick={onManage}
      className={clsx(isMobile && "w-full")}
      data-testid="program_cap_modal_manage_button"
    >
      {t("subIBReport_capModalManage", "Manage programs")}
    </ButtonV2>
  );

  const illustration = (
    <AlertTriageIcon
      className="h-16 w-16"
      aria-hidden="true"
      focusable="false"
    />
  );

  const copy = (
    <>
      <Typo
        element="h2"
        type={isMobile ? "headingLG" : "headingXL"}
        textColor="text-text-primary"
      >
        {t(
          "subIBReport_capModalTitle",
          "You've reached the {{cap}}-program limit",
          { cap },
        )}
      </Typo>
      <Typo
        element="p"
        type={isMobile ? "bodySM" : "bodyMD"}
        textColor="text-text-secondary"
      >
        {t(
          "subIBReport_capModalLead",
          "To add a new rebate program, close one you no longer need.",
        )}
      </Typo>
      <Typo
        element="p"
        type={isMobile ? "bodyXS" : "bodySM"}
        textColor={isMobile ? "text-text-tertiary" : "text-text-secondary"}
      >
        {withSupportLink(
          t(
            "subIBReport_capModalHelp",
            "Need more than {{cap}} programs? Contact your account manager or support@pepperstonepartners.com to request a higher limit.",
            { cap },
          ),
        )}
      </Typo>
    </>
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={open}
        onClose={onClose}
        isFullScreen
        isDismissable={false}
        showCloseButton
        closeButtonTestId="program_cap_modal_close_button"
        contentContainerClassName="py-3"
        footer={manageButton}
      >
        <div className="flex flex-col gap-4 pb-1">
          {illustration}
          {copy}
        </div>
      </BottomSheet>
    );
  }

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      isMobile={false}
      headerClassName="!hidden"
      // `!` beats ResponsiveModal's hardcoded `mt-6`.
      footerContainerClassName="!mt-8"
      footer={<div className="flex justify-end">{manageButton}</div>}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={t("common_close", "Close")}
              onClick={onClose}
              data-testid="program_cap_modal_close_button"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
            >
              <CrossIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {illustration}
        </div>
        {copy}
      </div>
    </ResponsiveModal>
  );
};
